import { useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import { counterClasses } from '@liquidweb-dev/undercurrent/Counter';
import ListItem from '@liquidweb-dev/undercurrent/ListItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import LWForm, { FormSubmitHandler } from 'components/LWForm';
import HookedCounter from 'components/standard/Form/HookedCounter';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import HookedTextField from 'components/standard/Form/HookedTextField';
import dayjs from 'dayjs';
import useSecureDataShareCreate from 'modules/queries/secureDataShare/useCreate';
import { useDialogSecureNotesContext } from './context';

export interface CreateNoteFormProps {
	onCancel: () => void;
	onSuccess: () => void;
}

const CreateNoteForm = ({ onCancel, onSuccess }: CreateNoteFormProps) => {
	const { unlockedId } = useDialogSecureNotesContext();
	const initValues = {
		description: '',
		expiration: '',
		secret: '',
		ticket: '',
		views: '0',
	};

	const [formValues, setFormValues] = useState(initValues);
	const [formErrors, setFormErrors] = useState({});

	const { mutate: createSecureNote, isLoading: isCreatingSecureNote } =
		useSecureDataShareCreate();

	const handleCancel = () => {
		onCancel();
		setFormValues(initValues);
		setFormErrors({});
	};

	const handleSubmit: FormSubmitHandler<typeof formValues> = ({
		description,
		secret,
		expiration,
		ticket,
		views,
	}) => {
		const currentTime = dayjs().format('HH:mm:ss');
		const formattedExpiration = expiration
			? dayjs(expiration).format(`YYYY-MM-DD ${currentTime}`)
			: undefined;

		createSecureNote(
			{
				description: description.length ? description : undefined,
				secret,
				views: Number(views) > 0 ? views : undefined,
				expiration: formattedExpiration,
				ticket: ticket.length ? ticket : undefined,
			},
			{
				onSuccess: () => {
					onSuccess();
					setFormValues(initValues);
				},
			},
		);
	};

	return (
		<ListItem sx={{ backgroundColor: ({ vars }) => vars.palette.uc.bg.weak }}>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				onErrorsChange={setFormErrors}
				style={{ width: '100%' }}
				onSubmit={handleSubmit}
				validators={{
					description: (value) => {
						if (!value) return 'Note title is required';
						return '';
					},
					secret: (value) => {
						if (!value) return 'Note content is required';
						return '';
					},
				}}
			>
				<Stack sx={{ gap: 200 }}>
					<HookedTextField
						name="description"
						label="Note title"
						placeholder="Name your note"
						sx={{ width: '100%', maxWidth: { md: '568px' } }}
					/>

					<Stack sx={{ flexDirection: { md: 'row' }, gap: 150 }}>
						<HookedCounter
							name="views"
							label="Max views"
							counterSize="small"
							sx={({ vars }) => ({
								[`& .${counterClasses.actions}`]: {
									backgroundColor: vars.palette.uc.bg.main,
								},
							})}
						/>
						<HookedTextField
							name="expiration"
							label="Expiration date"
							placeholder="Choose a date"
							type="date"
						/>
						<HookedTextField
							name="ticket"
							label="Related case number"
							placeholder="i.e. 21234334"
						/>
					</Stack>

					<HookedTextField
						name="secret"
						multiline
						minRows={5}
						label="Note content"
						placeholder="Drop your note here"
					/>

					<Stack
						sx={{
							flexDirection: { md: 'row' },
							gap: 100,
							alignItems: { md: 'start' },
						}}
					>
						<Tooltip
							title={
								unlockedId
									? 'You’re currently viewing a Secure Note. Lock it to continue.'
									: false
							}
						>
							<div>
								<HookedSubmit
									color="highlight"
									loading={isCreatingSecureNote}
									disabled={Boolean(unlockedId)}
									sx={{ width: '100%' }}
								>
									Save
								</HookedSubmit>
							</div>
						</Tooltip>

						<Button variant="secondary" onClick={handleCancel}>
							Cancel
						</Button>
					</Stack>
				</Stack>
			</LWForm>
		</ListItem>
	);
};

export default CreateNoteForm;
